import { DeleteIcon } from "@rothoblaas/design-system";
import { FC, memo, MouseEvent, useCallback, useContext, useMemo } from "react";
import ExpansionPanel from "../../../components/ui/ExpansionPanel";
import Icon from "../../../components/ui/Icon";
import NotificationDispatch, { showErrorNotification } from "../../../context/notificationContext";
import useI18n from "../../../hooks/useTranslations";
import { CalculationEntry } from "../../../http/concreteResin/types/calculationEntries";
import { useCalculationEntriesApi } from "../../../http/concreteResin/useCalculationEntries";
import { useConcreteResinSettings } from "../../../http/concreteResin/useSettings";
import { getNumberFormat } from "../../../utils/numberFormat";
import { formatEntryTitle, groupCalculationEntries } from "./calculationEntryGroups";
import styles from "./CalculationEntriesList.module.css";
import { useIsImperial } from "./resinUnits";

type Props = {
  calculationId: string;
  /** The calculation's saved entries (useCalculationEntries) */
  entries: CalculationEntry[] | undefined;
  selectedEntryId: string | null;
  onLoadEntry: (entry: CalculationEntry) => void;
  readOnly?: boolean;
};

const CalculationEntriesList: FC<Props> = ({ calculationId, entries, selectedEntryId, onLoadEntry, readOnly }) => {
  const api = useCalculationEntriesApi();
  const i18n = useI18n();
  const resinTranslation = i18n.translation.concreteResinModule;
  const dispatch = useContext(NotificationDispatch);
  // entries are saved in mm: shown in the calculation's current unit system
  const isImperial = useIsImperial();

  // The calculation's settings, shared with the form: the anchors entries were saved with are in
  // them whatever their visibility.
  const { data: settings } = useConcreteResinSettings(calculationId);
  const groups = useMemo(
    () => groupCalculationEntries(entries ?? [], settings?.chemicalAnchors ?? []),
    [entries, settings],
  );

  // A delete shows at once: a failure is only reported, the entry stays removed.
  const handleDelete = useCallback(
    (e: MouseEvent, entryId: string) => {
      e.stopPropagation();
      api.deleteEntry(calculationId, entryId).catch((err) => dispatch(showErrorNotification(i18n, err)));
    },
    [api, calculationId, dispatch, i18n],
  );

  if (!calculationId) return null;

  const locale = i18n.translation.languageCode;
  const formatLiters = (liters: number) =>
    `${getNumberFormat(locale, { maximumFractionDigits: 2 }).format(liters)} ${resinTranslation.unitLiters}`;

  return (
    <div className={styles.entriesSection}>
      {!entries || entries.length === 0 ? (
        <div className={styles.emptyState}>{resinTranslation.noCalculationsSaved}</div>
      ) : (
        groups.map((group) => {
          const meta = [
            group.totalLiters > 0 ? formatLiters(group.totalLiters) : null,
            group.cartridges > 0 ? `${group.cartridges} ${resinTranslation.cartridgesShort}` : null,
          ].filter(Boolean);

          return (
            <ExpansionPanel
              key={group.anchor.id}
              title={
                group.anchor.description ? `${group.anchor.code} — ${group.anchor.description}` : group.anchor.code
              }
              meta={meta.length > 0 ? meta.join(" | ") : undefined}
            >
              {group.entries.map((entry) => (
                <div
                  key={entry.id}
                  className={`${styles.entryCard} ${entry.id === selectedEntryId ? styles.entryCardSelected : ""}`}
                  onClick={() => onLoadEntry(entry)}
                >
                  <span className={styles.entryName}>
                    {formatEntryTitle(entry, isImperial, resinTranslation.holes)}
                    {/* the overage factor, none at 0: 10% waste */}
                    {!!entry.overageFactor && (
                      <span className={styles.entryParams}>
                        {` | ${Math.round(entry.overageFactor)}% ${resinTranslation.waste}`}
                      </span>
                    )}
                  </span>
                  <span className={styles.entryResults}>
                    {entry.estimatedLiters != null ? formatLiters(entry.estimatedLiters) : "—"}
                  </span>

                  {!readOnly && (
                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={(e) => handleDelete(e, entry.id)}
                      title={i18n.translation.common.delete}
                    >
                      <Icon glyph={DeleteIcon} />
                    </button>
                  )}
                </div>
              ))}
            </ExpansionPanel>
          );
        })
      )}
    </div>
  );
};

export default memo(CalculationEntriesList);
